import { type FC, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicNavBar from '../components/layout/PublicNavBar';
import PublicDocsSidebar from './docs/PublicDocsSidebar';
import PublicDocsContent from './docs/PublicDocsContent';
import { getDoc } from '../docs';
import { usePageMeta } from '../hooks/usePageMeta';

const DEFAULT_DOC_ID = 'getting-started/overview';

const DocsLayout: FC<{ activeDocId: string; children: ReactNode }> = ({ activeDocId, children }) => (
  <div className="min-h-screen bg-white text-gray-900 font-[Poppins]">
    <PublicNavBar />
    <PublicDocsSidebar activeDocId={activeDocId} />
    <div className="pt-16 lg:pl-72">
      {children}
    </div>
  </div>
);

const FALLBACK_DESCRIPTION = 'Quanthop documentation. Learn how to use the backtesting engine, QSL strategy language, walk-forward analysis, and optimization tools.';

const DocumentationPage: FC = () => {
  const params = useParams();
  const rawPath = params['*'];
  const docPath = rawPath || DEFAULT_DOC_ID;
  const page = getDoc(docPath);

  // Use a distinct title for /docs so it doesn't duplicate /docs/getting-started/overview
  const metaTitle = !rawPath ? 'Documentation' : page ? `${page.title} — Docs` : 'Documentation';
  const metaDescription = (!rawPath || !page?.description) ? FALLBACK_DESCRIPTION : page.description;
  usePageMeta({ title: metaTitle, description: metaDescription });

  if (!page) {
    return (
      <DocsLayout activeDocId={docPath}>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-950 mb-4">Page not found</h1>
          <p className="text-gray-500 mb-8">
            The documentation page you are looking for does not exist.
          </p>
          <Link
            to="/docs"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#9d4edd] hover:bg-[#8b3ec7] text-white rounded text-sm font-medium transition-colors"
          >
            Back to docs
          </Link>
        </div>
      </DocsLayout>
    );
  }

  return (
    <DocsLayout activeDocId={docPath}>
      <main className="px-6 sm:px-10 py-10">
        <PublicDocsContent page={page} />
      </main>
    </DocsLayout>
  );
};

export default DocumentationPage;
